// Form1.jsx
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useContext } from 'react';
import styles from './index.module.css';
import Popup from './newForm1Popup';
import yesnoImage from './yesorno.png';
import noImage from './circle-xmark-solid.png';
import yesImage from './circle-check-solid.png';
import { ThemeContext } from "../../context/ThemeContext";
import { authenticatedFetch } from "../../services/apiClient";
import lightLogo from '../home/logolong.png';
import darkLogo from '../home/OLDlogolong.png';

// <-- API base URL (change if needed) -->
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const Form1 = (props) => {
  const [logo, setLogo] = useState(lightLogo);
  const navigate = useNavigate();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [value, setValue] = useState("");
  const [valueType, setValueType] = useState("");
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [error, setError] = useState("");
  const [commonCategories, setCommonCategories] = useState([
    "Title",
    "Description",
    "Price",
    "Image"
  ]);
  const { isDarkMode } = useContext(ThemeContext);
  const [theme, setTheme] = useState(isDarkMode);

  useEffect(() => {
    setTheme(isDarkMode);
    setLogo(isDarkMode ? darkLogo : lightLogo);
    // Toggle the class on the body element based on the theme
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
      document.body.classList.remove('light-mode');
    } else {
      document.body.classList.add('light-mode');
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const controller = new AbortController();

    async function detectPriceCurrency() {
      try {
        const response = await fetch("https://ipapi.co/currency/", {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Currency lookup failed: ${response.status}`);
        }

        const currency = (await response.text()).trim().toUpperCase();
        if (!/^[A-Z]{3}$/.test(currency)) {
          throw new Error(`Unsupported or invalid detected currency: ${currency || "empty response"}`);
        }

        if (currency !== "USD") {
          const rateResponse = await fetch(
            `https://api.frankfurter.dev/v2/rate/USD/${currency}`,
            { signal: controller.signal }
          );
          if (!rateResponse.ok) {
            throw new Error(`Frankfurter does not have a USD exchange rate for ${currency}`);
          }

          const rate = await rateResponse.json();
          if (typeof rate.rate !== "number" || !Number.isFinite(rate.rate) || rate.rate <= 0) {
            throw new Error(`Frankfurter returned an invalid USD exchange rate for ${currency}`);
          }
        }

        setPriceCurrency(currency);
        localStorage.setItem("priceCurrency", currency);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Could not detect local currency; defaulting to USD:", error);
        setPriceCurrency("USD");
        localStorage.setItem("priceCurrency", "USD");
      }
    }

    detectPriceCurrency();
    return () => controller.abort();
  }, []);
  
  const [categories, setCategories] = useState(() => {
    const savedCategories = localStorage.getItem('form1Categories');
    const parsedCategories = savedCategories ? JSON.parse(savedCategories) : [];
    return parsedCategories.sort((a, b) => a.name.localeCompare(b.name));
  });

  const [question, setQuestion] = useState(() => {
    const savedQuestion = localStorage.getItem('form1Question');
    return savedQuestion ? JSON.parse(savedQuestion) : "";
  });
  const [priceCurrency, setPriceCurrency] = useState("USD");

  const handleQuestionChange = (event) => {
    setQuestion(event.target.value);
  };

  const displayed = { Title: false, Price: false, Image: false, Description: false };

  const commonCategoryNames = (commonCategories);
  const allCommonFeatures = Object.values(commonCategories).flat();

  function getValue(inputValue) {
    setValue(inputValue);
  }

  // -----------------------------
  // API helpers
  // -----------------------------
  async function uploadImageToServer(file) {
    if (!file) throw new Error("No file provided to uploadImageToServer");

    const formData = new FormData();
    formData.append("file", file);

    const res = await authenticatedFetch(`${BACKEND_URL}/api/project/image/add`, {
      method: "POST",
      body: formData
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`Upload failed: ${res.status} ${txt}`);
    }

    const data = await res.json();
    if (!data.success) throw new Error("Upload failed (no success flag)");

    // Expecting { success: true, url, filePath, token }
    return data;
  }

  async function deleteImageFromServer(filePath, url) {
    if (!filePath && !url) return;
    try {
      await fetch(`${BACKEND_URL}/api/project/image/delete`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filePath, url })
      });
    } catch (err) {
      console.error("Failed to delete image via API:", err);
    }
  }

  // -----------------------------
  // when `value` changes, handle it
  // -----------------------------
  useEffect(() => {
    if (!value) return;

    const apply = async () => {
      try {
        switch (valueType) {
          case "Enter New Category":
            if (Array.isArray(value)) {
              addCategories(value);
            } else {
              addCategory(value);
            }
            break;
          case "Enter New Feature":
            await addFeature(); // async
            break;
          default:
            break;
        }
      } catch (err) {
        console.error("Error applying value:", err);
        setError("Error processing feature. See console for details.");
        setTimeout(() => setError(""), 8000);
      } finally {
        setValue("");
        setValueType("");
      }
    };

    apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  
    if (categories.length === 0) {
      const defaultCategories = [
        { id: generateId(), name: "Title", features: [] },
        { id: generateId(), name: "Image", features: [] },
        { id: generateId(), name: "Description", features: [] },
        { id: generateId(), name: "Price", features: [] },
      ];
  
      const customSortOrder = ["Title", "Image", "Description", "Price"];
  
      const sortedCategories = defaultCategories.sort(
        (a, b) => customSortOrder.indexOf(a.name) - customSortOrder.indexOf(b.name)
      );
  
      setCategories(sortedCategories);
      localStorage.setItem("form1Categories", JSON.stringify(sortedCategories));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addCategory(name) {
    const id = generateId();
    setCategories((prev) => {
      const updatedCategories = [...prev, { id, name, features: [] }];
      return updatedCategories.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  function addCategories(values) {
    setCategories((prev) => {
      const newCategories = values.map((categoryName) => ({
        id: generateId(),
        name: categoryName,
        features: [],
      }));
      const updatedCategories = [...prev, ...newCategories];
      return updatedCategories.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  // -----------------------------
  // addFeature: supports File, array of Files/strings, single string
  // -----------------------------
  async function addFeature() {
    if (!currentCategoryId) return;

    const category = categories.find(c => c.id === currentCategoryId);
    if (!category) return;

    // Make a shallow copy for immutability
    let newCategories = categories.map(c => ({ ...c, features: [...(c.features || [])] }));

    if (Array.isArray(value)) {
      // Array can contain Files or strings
      for (const val of value) {
        if (val instanceof File) {
          // upload & push image
          const uploaded = await uploadImageToServer(val);
          newCategories = newCategories.map(c => {
            if (c.id === currentCategoryId) {
              c.features.push({
                id: generateId(),
                name: uploaded.url,
                filePath: uploaded.filePath,
                token: uploaded.token,
                type: "image"
              });
            }
            return c;
          });
        } else {
          // plain string -> text feature (or if current category is Image and looks like URL treat as image URL)
          newCategories = newCategories.map(c => {
            if (c.id === currentCategoryId) {
              const isImageCategory = c.name.toLowerCase() === "image";
              const isUrl = typeof val === "string" && val.startsWith("http");
              if (isImageCategory && isUrl) {
                c.features.push({
                  id: generateId(),
                  name: val,
                  type: "image"
                });
              } else {
                c.features.push({
                  id: generateId(),
                  name: String(val),
                  type: "text"
                });
              }
            }
            return c;
          });
        }
      }
    } else {
      // single value (File or string)
      if (value instanceof File) {
        const uploaded = await uploadImageToServer(value);
        newCategories = newCategories.map(c => {
          if (c.id === currentCategoryId) {
            c.features.push({
              id: generateId(),
              name: uploaded.url,
              filePath: uploaded.filePath,
              token: uploaded.token,
              type: "image"
            });
          }
          return c;
        });
      } else {
        // string
        newCategories = newCategories.map(c => {
          if (c.id === currentCategoryId) {
            const isImageCategory = c.name.toLowerCase() === "image";
            const isUrl = typeof value === "string" && value.startsWith("http");
            if (isImageCategory && isUrl) {
              c.features.push({
                id: generateId(),
                name: value,
                type: "image"
              });
            } else {
              c.features.push({
                id: generateId(),
                name: String(value),
                type: "text"
              });
            }
          }
          return c;
        });
      }
    }

    // sort categories by name to keep consistent
    newCategories = newCategories.sort((a, b) => a.name.localeCompare(b.name));
    setCategories(newCategories);
  }

  const generateId = () => {
    return Math.random().toString(36).substring(2, 9);
  };

  

  function handleSubmit() {  
    if (!question.trim()) {
      setError("Please enter a question.");
      setTimeout(() => setError(""), 10000);
      return;
    }
  
    if (!question.trim().endsWith('?')) {
      setError("Please end the question with a '?' mark.");
      setTimeout(() => setError(""), 10000);
      return;
    }
  
    console.log("categories are as follows:", categories);
  
    // Ensure all required categories exist
    const requiredCategories = ['title', 'description', 'price', 'image'];
    let updatedCategories = [...categories];
    
    // Check and add missing categories
    requiredCategories.forEach(requiredCat => {
        if (!updatedCategories.some(cat => cat.name.toLowerCase() === requiredCat)) {
            updatedCategories.push({
                name: requiredCat,
                features: [{ name: " ", type: requiredCat === 'image' ? 'image' : 'text' }]
            });
        }
    });

    let titles = [];
    let descriptions = [];
    let prices = [];
    let images = [];
  
    // Iterate through each category
    updatedCategories.forEach((category) => {
      const lowerName = category.name.toLowerCase();
      const features = category.features.length
        ? category.features.map((feature) => {
            if (feature.type === "image") {
              console.log("Processing image feature:", feature);
              return feature.name; // now feature.name is the URL string
            }
            console.log("Processing feature:", feature);
            return feature.name;
          })
        : [" "];
  
      if (lowerName === "title") {
        titles = [...titles, ...features];
      } else if (lowerName === "description") {
        descriptions = [...descriptions, ...features];
      } else if (lowerName === "price") {
        prices = [...prices, ...features];
      } else if (lowerName === "image") {
        images = [...images, ...features];
      }
    });
  
    console.log("Titles:", titles);
    console.log("Descriptions:", descriptions);
    console.log("Prices:", prices);
    console.log("Images:", images);

    let errorSet = false;

    // Note: Character length validation is now handled on the backend
    // to ensure consistency across all clients (Dashboard, ClientAPI, etc.)

    if (errorSet) return;

    const generateQuestionFeatures = (categories) => {
      // Generate questionFeatures as array of {categoryName, featuresName} objects
      const questionFeaturesArray = [];
      
      // Ensure all required categories exist
      const requiredCategories = ['title', 'description', 'price', 'image'];
      let updatedCategories = [...categories];
      
      requiredCategories.forEach(requiredCat => {
        if (!updatedCategories.some(cat => cat.name.toLowerCase() === requiredCat)) {
          updatedCategories.push({
            name: requiredCat,
            features: [{ name: " ", type: requiredCat === 'image' ? 'image' : 'text' }]
          });
        }
      });

      updatedCategories.forEach((category) => {
        const categoryName = category.name.toLowerCase();
        const features = category.features.length
          ? category.features.map((feature) => {
              if (feature.type === "image") {
                console.log("Processing image feature:", feature);
                return feature.name; // URL string
              }
              return feature.name;
            })
          : [" "];
    
        // Add each feature as an entry in the questionFeatures array
        features.forEach((featuresName) => {
          questionFeaturesArray.push({
            categoryName: categoryName,
            featuresName: featuresName || " ",
          });
        });
      });

      return questionFeaturesArray;
    };
    
    if (categories.length === 0) {
      const formData1 = [];
      localStorage.setItem("questionFeatures", JSON.stringify(formData1));
    } else {
      const formattedData = generateQuestionFeatures(categories);
      localStorage.setItem("questionFeatures", JSON.stringify(formattedData));
    }
    
    localStorage.setItem("form1Categories", JSON.stringify(categories));
    localStorage.setItem("form1Question", JSON.stringify(question));
    
    navigate("/form2");
  }
  
  useEffect(() => {
    localStorage.setItem('form1Categories', JSON.stringify(categories));
    localStorage.setItem('form1Question', JSON.stringify(question));
  }, [categories, question]);

  // -----------------------------
  // Cancel: delete uploaded images via API, then clear storage and navigate
  // -----------------------------
  async function handleCancel() {
    const storedData = JSON.parse(localStorage.getItem('form1Categories'));
    console.log("storedData", storedData);
  
    if (storedData && storedData.length > 0) {
      for (const category of storedData) {
        if (category.features && category.features.length > 0) {
          for (const feature of category.features) {
            // If this feature is an image, attempt to delete via API
            if (category.name === "Image" && feature) {
              const imageURL = feature.name;
              const filePath = feature.filePath;
              if ((filePath || imageURL) && imageURL?.trim() !== '') {
                try {
                  await deleteImageFromServer(filePath, imageURL);
                  console.log("Image deleted successfully via API:", imageURL);
                } catch (error) {
                  console.error("Error occurred while deleting image via API:", error);
                }
              } else {
                console.log('No valid image URL found for item, skipping deletion.');
              }
            }
          }
        }
      }
    }
  
    localStorage.removeItem("form1Categories");
    localStorage.removeItem("form1Question");
    localStorage.removeItem("questionFeatures");
    localStorage.removeItem("formData2");
    localStorage.removeItem("priceCurrency");
    navigate("/");
  }

  function handleDeleteCategory(categoryId) {
    setCategories((prev) => prev.filter((category) => category.id !== categoryId));
  }

  async function handleDeleteFeature(categoryId, featureId) {
    const cat = categories.find(c => c.id === categoryId);
    const feature = cat?.features.find(f => f.id === featureId);

    if (cat?.name === "Image" && feature) {
      try {
        await deleteImageFromServer(feature.filePath, feature.name);
        console.log("Deleted image via API:", feature.name);
      } catch (err) {
        console.error("Failed to delete image via API:", err);
      }
    }

    setCategories((prevCategories) =>
      prevCategories.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              features: category.features.filter(
                (feature) => feature.id !== featureId
              ),
            }
          : category
      )
    );
  }

  const extractImageFilename = (url) => {
    // Decode the URL to handle encoded characters
    try {
      const decodedUrl = decodeURIComponent(url);

      // Regex to match everything after '/o/' or last '/' and then capture a filename with extension
      const regex = /\/[^\/]+\/(?:[0-9a-fA-F-]{36}-)?([^\/]+?\.(jpg|jpeg|png|gif|webp|svg))/i;
      const match = decodedUrl.match(regex);
      if (!match) {
        // fallback: try grabbing last path segment
        const lastSegMatch = decodedUrl.match(/\/([^\/?#]+)(?:[?#]|$)/);
        if (lastSegMatch) return lastSegMatch[1];
        return null;
      }

      // Extract the filename with extension
      const filename = match[1];
      return filename;
    } catch (err) {
      console.log("extractImageFilename error", err);
      return null;
    }
  };
  const backgroundColorDarkMode = "#141414";
  const formatPrice = (value) => {
    const text = String(value).trim();
    const amount = Number(text);
    if (!text) return "";
    if (!Number.isFinite(amount)) return `${priceCurrency} ${text}`;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: priceCurrency,
    }).format(amount);
  };

  return (
    <>
        <header>
        <span></span>
        <img
            src={logo}  
            alt="Linesol"
            style={{ height: '44px' }}
          />
        <span></span>
      </header>
      
    <div className={styles.responsiveContainer}>
      
      {/* Empty Column (Column 1) */}
      <div></div>

      <div className={styles.firstDiv}>
      {/* First Div (Column 2) - Centered Horizontally */}
      <div
        style={{
          maxWidth: "600px", // Optional max width
          textAlign: "center",
        }}
      >
        {isPopupVisible && (
          <Popup
            getValue={getValue}
            valueType={valueType}
            category={
              valueType === "Enter New Category" ? commonCategoryNames : null
            }
            feature={
              valueType === "Enter New Feature"
                ? commonCategories[
                categories.find((cat) => cat.id === currentCategoryId)?.name
                ] || allCommonFeatures
                : null
            }
            priceCurrency={priceCurrency}
            selectedCategory={
              valueType === "Enter New Category"
                ? null
                : categories.find((cat) => cat.id === currentCategoryId)?.name
            }
            setIsPopupVisible={setIsPopupVisible}
          />
        )}


        <div className={styles.form} style={{
            boxShadow: theme
              ? 'rgba(52, 51, 51, 0.44) 0px 0px 0px 2px' // border for dark mode
              : 'rgba(201, 201, 201, 0.23) 0px 0px 0px 2px', // border for light mode
          }}>
          <div className={styles.headerTop}>
            <p style={{ color: "#007bff" }}>Question Features</p>
            <p>User Features</p>
            <p>Study Metadata</p>
          </div>

          <p className={styles.questionText} style={{ marginTop: "0px", textAlign: "justify" }}>
            Define a single yes/no question.
            Then add context to this question by configuring one or more question
            card features: Titles, Images, Descriptions, and Prices. Each
            participant responds to one of the possible configurations.
          </p>
          <div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <p style={{textDecoration: "underline" }}>
                Question
              </p>
            </div>
            <input
              type="text"
              value={question}
              onChange={handleQuestionChange}
              placeholder="e.g. Would you buy this product?" //e.g. Would you buy this product?
              className={styles.inputQuestion} 
              style={{ background: theme ? "black" : "white", color: theme ? "white" : "black" }}
              ></input>
            {error && (
              <div style={{ color: "red", marginTop: "5px" }}>{error}</div>
            )}
          </div>
          <table
            className="category-table"
            style={{
              border: `1px solid`,
              borderColor: theme ? '#e1e1e1' : '#4A4A4A',
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th style={{ width: "auto", border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A'  }}>Category</th>
                <th style={{ width: "auto", border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A'  }}>Features</th>
                <th style={{ width: "20px", border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A'  }}>Delete</th>
              </tr>
            </thead>
            <tbody>
              {categories
                .slice()
                .sort(
                  (a, b) =>
                    ["Title", "Image", "Description", "Price"].indexOf(a.name) -
                    ["Title", "Image", "Description", "Price"].indexOf(b.name)
                )
                .flatMap((category) =>
                  category.features.length > 0
                    ? category.features.map((feature) => {
                        // Determine if this feature is an image
                        const isImageUrl = category.name === "Image";
                        let displayName = feature.name;

                        if (isImageUrl) {
                          // Use the extractImageFilename function to get the original filename
                          const extractedFilename = extractImageFilename(feature.name);
                          if (extractedFilename) {
                            displayName = extractedFilename;
                          }
                        }
                        return (
                          <tr key={feature.id} style={{ border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A'  }}>
                            <td style={{ color: theme ? "white" : "black", border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A'  }}>{category.name}</td>
                            <td style={{ border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A'  }}>
                              {isImageUrl ? (
                                <>
                                  <a href={feature.name} target="_blank" rel="noopener noreferrer" style={{ color: theme ? "white" : "black" }}>
                                    {displayName}
                                  </a>
                                </>
                              ) : (
                                <span>{displayName}</span>
                              )}
                            </td>
                            <td className="delete-cell" style={{ border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A'  }}>
                              <button
                                style={{ width: "30px", height: "25px" }}
                                className="delete-button"
                                onClick={() => handleDeleteFeature(category.id, feature.id)}
                              >
                                X
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    : [
                        <tr key={category.id} style={{ border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A'  }}>
                          <td style={{ color: theme ? "white" : "black", border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A'  }}>{category.name}</td>
                          <td style={{ border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A'  }}>
                            <Link
                              onClick={() => {
                                setIsPopupVisible(true);
                                setValueType("Enter New Feature");
                                setCurrentCategoryId(category.id);
                              }}
                              style={{ color: "#007bff" }}
                            >
                              + New Feature
                            </Link>
                          </td>
                          <td className="delete-cell" style={{ border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A' }}>
                            <button
                              style={{ width: "30px", height: "25px" }}
                              className="delete-button"
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              X
                            </button>
                          </td>
                        </tr>,
                      ]
                )}
              <tr style={{ border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A' }}>
                <td style={{ border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A' }}>
                  <Link
                    onClick={() => {
                      setIsPopupVisible(true);
                      setValueType("Enter New Category");
                    }}
                    style={{ color: "#007bff" }}
                  >
                    + New Category
                  </Link>
                </td>
                <td style={{ border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A' }}></td>
                <td style={{ border: `1px solid`, borderColor: theme ? '#e1e1e1' : '#4A4A4A' }}></td>
              </tr>
            </tbody>
          </table>
          <div style={{ display: "flex", columnGap: "10px", marginTop: "20px" }}>
            <button
              onClick={handleCancel}
              style={{
                padding: "5px 20px",
                border: "none",
                borderRadius: "5px",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              style={{
                padding: "5px 20px",
                border: "none",
                borderRadius: "5px",
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      </div>

      <div className={styles.secondDiv}>
      {/* Second Div (Column 3) - On the Right#d3f3d3 */}
      <div className={styles.cardContainer} style={{
          backgroundColor: theme ? backgroundColorDarkMode : "white",
          boxShadow: theme
            ? 'rgb(255, 255, 255) 0px 0px 0px 2px' // border for dark mode
            : 'rgb(0, 0, 0) 0px 0px 0px 2px', // border for light mode
        }}
      >
      <h3 style={{ marginBottom: 10}}>Example Question Card</h3>
      <div className={styles.content} style={{backgroundColor: theme ? "#FAF9F6" : "#f5f5f5", borderWidth: 2, borderColor: "#dedede", borderStyle: "solid"}}>
        <h3 style={{ fontSize: "1.3em",  margin: 0, justifyContent: "center", textDecoration: "underline", color: "black"}}>{question}</h3>
        {categories
          .sort((a, b) => {
            const order = ["Title", "Image", "Description","Price"];
            return order.indexOf(a.name) - order.indexOf(b.name);
          })
          .map((category) => {
            if (displayed[category.name]) return null;
            if (category.features.length === 0) return null;

            displayed[category.name] = true;

            return (
              <div key={category.id} style={{ marginTop: "10px" }}>
                
                {category.name === "Title" && (
                  <h3 style={{color: "black"}}>
                    {category.features[0].name}
                  </h3>
                )}

                {category.name === "Image" && (
                  <div
                    style={{
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={category.features[0].url || category.features[0].name}
                      alt="Product"
                      style={{ width: "100%", height: "auto" }}
                    />
                  </div>
                )}

                {category.name === "Description" && (
                  <div style={{ marginTop: "15px" }}>
                    <h3 style={{color: "black"}}>
                      {category.features[0].name}
                    </h3>
                  </div>
                )}
                {category.name === "Price" && (
                  <div
                    style={{
                      textAlign: "center", // Center the text content
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "1.2em",
                        margin: 0,
                        color:"black",
                        display: "inline-block", // Ensure the h2 is treated as a block for centering
                      }}
                    >
                      {formatPrice(category.features[0].name)}
                    </h3>
                  </div>
                )}
              </div>
            );
          })}
      </div>
      <div className={styles.bottomImage}>
        <img
          src={noImage}
          alt="No Image"
          className={styles.imagebottom}
        />
        <img
          src={yesImage}
          alt="Yes Image"
          className={styles.imagebottom}
        />
      </div>
    </div>
    </div>
    </div>
    </>
  );
};

export default Form1;
